import React, { useState } from 'react';
import { NewspaperIcon, ExternalLinkIcon, PlusIcon, MinusIcon, BookOpenIcon } from 'lucide-react';
export const MediaRecognition = () => {
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const researchPublications = [{
    title: "Journal of Women's Health",
    excerpt: 'Biofilm formation in the vaginal microbiome: Implications for recurrent bacterial vaginosis and treatment resistance',
    authors: 'Chen E, Rodriguez M, et al.',
    date: 'March 2023',
    icon: <svg width="40" height="40" viewBox="0 0 24 24" className="text-indigo-700" data-id="element-2690">
          <path fill="currentColor" d="M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M19,5V19H5V5H19Z" data-id="element-2691" />
          <path fill="currentColor" d="M14,17H7V15H14V17M17,13H7V11H17V13M17,9H7V7H17V9Z" data-id="element-2692" />
        </svg>
  }, {
    title: 'Menopause: The Journal of The North American Menopause Society',
    excerpt: 'Enzyme-based approaches to vaginal dryness: A comprehensive review of clinical evidence',
    authors: 'Wilson A, Johnson S, et al.',
    date: 'June 2023',
    icon: <svg width="40" height="40" viewBox="0 0 24 24" className="text-blue-700" data-id="element-2693">
          <path fill="currentColor" d="M12,3L1,9L12,15L21,10.09V17H23V9M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z" data-id="element-2694" />
        </svg>
  }, {
    title: 'JAMA Network Open',
    excerpt: 'Comparative effectiveness of biofilm-disrupting enzymes versus traditional treatments for vaginal health conditions: A randomized controlled trial',
    authors: 'Johnson S, Lee P, et al.',
    date: 'September 2023',
    icon: <svg width="40" height="40" viewBox="0 0 24 24" className="text-green-700" data-id="element-2695">
          <path fill="currentColor" d="M16,2H4A2,2 0 0,0 2,4V20A2,2 0 0,0 4,22H16A2,2 0 0,0 18,20V4A2,2 0 0,0 16,2M16,20H4V4H16V20M19,4H20V20H19M22,4H23V20H22" data-id="element-2696" />
        </svg>
  }, {
    title: 'Microbiome',
    excerpt: 'The role of specific probiotic strains in restoring and maintaining vaginal microbiome health: Mechanisms and clinical applications',
    authors: 'Rodriguez M, Wilson A, et al.',
    date: 'November 2023',
    icon: <svg width="40" height="40" viewBox="0 0 24 24" className="text-purple-700" data-id="element-2697">
          <path fill="currentColor" d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.1 14.8,9.5V11C15.4,11 16,11.6 16,12.3V15.8C16,16.4 15.4,17 14.7,17H9.2C8.6,17 8,16.4 8,15.7V12.2C8,11.6 8.6,11 9.2,11V9.5C9.2,8.1 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z" data-id="element-2698" />
        </svg>
  }];
  const toggleExpand = (index: number) => {
    setExpandedItem(expandedItem === index ? null : index);
  };
  return <section className="my-12" data-id="element-2699">
      <h2 className="text-2xl font-serif font-bold text-slate-900 mt-10 mb-5 leading-tight border-b border-slate-200 pb-2 flex items-center" data-id="element-2700">
        <BookOpenIcon className="h-5 w-5 text-slate-700 mr-2 inline" data-id="element-2701" />
        Peer-Reviewed Research Publications
      </h2>
      <p className="text-base text-slate-800 leading-relaxed mb-6" data-id="element-2702">
        Our content is based on scientific research published in respected
        medical journals. Here are some key studies that inform our educational
        materials about women's health.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2703">
        {researchPublications.map((item, index) => <div key={index} className={`bg-white rounded-lg border transition-all duration-300 overflow-hidden ${expandedItem === index ? 'border-indigo-300 shadow-md' : 'border-slate-200 hover:border-indigo-200'}`} data-id="element-2704">
            <div className="p-4 flex items-start cursor-pointer" onClick={() => toggleExpand(index)} data-id="element-2705">
              <div className="mr-4 flex-shrink-0" data-id="element-2706">{item.icon}</div>
              <div className="flex-grow" data-id="element-2707">
                <div className="flex justify-between items-start" data-id="element-2708">
                  <h3 className="font-serif font-semibold text-slate-900 text-sm" data-id="element-2709">
                    {item.title}
                  </h3>
                  <button className="text-slate-400 hover:text-slate-600" aria-label={expandedItem === index ? 'Collapse' : 'Expand'} data-id="element-2710">
                    {expandedItem === index ? <MinusIcon className="h-5 w-5" data-id="element-2711" /> : <PlusIcon className="h-5 w-5" data-id="element-2712" />}
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-2713">
                  {item.date} • {item.authors}
                </div>
              </div>
            </div>
            <div className={`px-4 pb-4 pt-0 transition-all duration-300 ${expandedItem === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-2714">
              <div className="pl-14" data-id="element-2715">
                <p className="text-sm text-slate-700 mb-3" data-id="element-2716">{item.excerpt}</p>
                <p className="text-xs text-slate-500 mb-3" data-id="element-2717">
                  This peer-reviewed research provides valuable insights into
                  mechanisms and evidence-based approaches for women's health
                  issues.
                </p>
                <button className="text-indigo-600 hover:text-indigo-800 text-sm inline-flex items-center" onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }} data-id="element-2718">
                  View research summary
                  <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-2719" />
                </button>
              </div>
            </div>
          </div>)}
      </div>
      <div className="mt-6 bg-slate-50 p-4 rounded-lg border border-slate-200" data-id="element-2720">
        <h3 className="font-medium text-slate-900 text-sm mb-2" data-id="element-2721">
          Understanding Research Quality
        </h3>
        <p className="text-sm text-slate-600" data-id="element-2722">
          When evaluating women's health information, it's important to consider
          the quality of the research. We prioritize randomized controlled
          trials, systematic reviews, and studies published in peer-reviewed
          journals with appropriate sample sizes and methodologies.
        </p>
      </div>
    </section>;
};